import { Head, Link, router } from "@inertiajs/react";
import {
  Box,
  Button,
  Callout,
  Container,
  Flex,
  Heading,
  Tabs,
  Text,
} from "@radix-ui/themes";
import {
  AlertCircle,
  ArrowLeft,
  ExternalLink,
  ImagePlus,
  Info,
} from "lucide-react";
import { ManagerGalleryShowProps } from "@/types/manager_gallery";
import GalleryAppearanceForm from "@/shared/manager/gallery/GalleryAppearanceForm";
import GalleryDetailsForm from "@/shared/manager/gallery/GalleryDetailsForm";
import ImageGrid from "@/shared/manager/gallery/ImageGrid";
import ImageUploader from "@/shared/manager/gallery/ImageUploader";
import PlaylistEditor from "@/shared/manager/gallery/PlaylistEditor";
import StorageMeter from "@/shared/manager/gallery/StorageMeter";

export default function GalleryShow({
  event,
  gallery,
  denial,
  storage,
  appearance,
  limits,
  paths,
  errors = [],
}: ManagerGalleryShowProps) {
  // A clubless event can't reach this page — a club manager's scope is their
  // club's events — so treat a missing quota as no room rather than infinite.
  const remainingBytes = storage
    ? Math.max(storage.quota_bytes - storage.used_bytes, 0)
    : 0;

  return (
    <>
      <Head title={`Gallery · ${event.name}`} />
      <Container size="4" py="6" px="4">
        <Flex direction="column" gap="5">
          <Flex justify="between" align="end" gap="3" wrap="wrap">
            <Flex direction="column" gap="1">
              <Button
                asChild
                variant="ghost"
                color="gray"
                size="1"
                style={{ alignSelf: "flex-start" }}
              >
                <Link href={paths.index}>
                  <ArrowLeft size={14} />
                  Back to Events
                </Link>
              </Button>
              <Heading size="6">{event.name}</Heading>
              <Text size="2" color="gray">
                Gallery
              </Text>
            </Flex>

            {/* A plain anchor, not an Inertia Link: the public gallery is a
                HAML page in the final_bout namespace, not an Inertia route.
                New tab because the manager is usually mid-edit. */}
            {paths.public_gallery ? (
              <Button asChild variant="soft">
                <a
                  href={paths.public_gallery}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink size={15} />
                  View live gallery
                </a>
              </Button>
            ) : (
              // Slugs are typed by hand, so this is a real state rather than a
              // defensive branch — and the fix is one field away.
              <Text size="1" color="gray">
                <Link href={event.edit_path}>Give this event a URL slug</Link>{" "}
                to publish its gallery.
              </Text>
            )}
          </Flex>

          {errors.length > 0 && (
            <Callout.Root color="red">
              <Callout.Icon>
                <AlertCircle size={16} />
              </Callout.Icon>
              <Callout.Text>{errors.join(". ")}</Callout.Text>
            </Callout.Root>
          )}

          {/* Three states, in the order they actually happen: refused, not
              created yet, or here. */}
          {denial ? (
            <Callout.Root color="amber">
              <Callout.Icon>
                <Info size={16} />
              </Callout.Icon>
              <Callout.Text>
                {denial} An admin can change that from the club's settings.
              </Callout.Text>
            </Callout.Root>
          ) : !gallery ? (
            <Callout.Root>
              <Callout.Icon>
                <ImagePlus size={16} />
              </Callout.Icon>
              <Callout.Text>
                <Flex direction="column" gap="3" align="start">
                  <Text>This event doesn't have a gallery yet.</Text>
                  <Button onClick={() => router.post(paths.create)} size="2">
                    Create gallery
                  </Button>
                </Flex>
              </Callout.Text>
            </Callout.Root>
          ) : (
            <Tabs.Root defaultValue="images">
              {/* Above the tabs, not inside one: the quota covers images and
                  audio together, so it would be wrong to imply either tab has
                  its own allowance. */}
              {storage && (
                <Box mb="4">
                  <StorageMeter storage={storage} />
                </Box>
              )}
              <Tabs.List>
                <Tabs.Trigger value="images">
                  Images ({gallery.images.length})
                </Tabs.Trigger>
                <Tabs.Trigger value="playlist">
                  Playlist ({gallery.songs.length})
                </Tabs.Trigger>
                <Tabs.Trigger value="details">Details</Tabs.Trigger>
                <Tabs.Trigger value="appearance">Appearance</Tabs.Trigger>
              </Tabs.List>

              <Tabs.Content value="images">
                <Flex direction="column" gap="4" pt="4">
                  <ImageUploader
                    directUploadPath={paths.direct_upload}
                    imagesPath={paths.images}
                    remainingBytes={remainingBytes}
                  />
                  <ImageGrid
                    images={gallery.images}
                    reorderPath={paths.reorder_images}
                  />
                </Flex>
              </Tabs.Content>

              <Tabs.Content value="playlist">
                <Flex direction="column" gap="4" pt="4">
                  <PlaylistEditor
                    songs={gallery.songs}
                    directUploadPath={paths.direct_upload}
                    songsPath={paths.songs}
                    reorderPath={paths.reorder_songs}
                    remainingBytes={remainingBytes}
                  />
                </Flex>
              </Tabs.Content>

              <Tabs.Content value="details">
                <Flex direction="column" gap="4" pt="4">
                  <GalleryDetailsForm
                    gallery={gallery}
                    limits={limits}
                    updatePath={paths.update}
                  />
                </Flex>
              </Tabs.Content>

              <Tabs.Content value="appearance">
                <Flex direction="column" gap="4" pt="4">
                  <GalleryAppearanceForm
                    gallery={gallery}
                    appearance={appearance}
                    eventName={event.name}
                    clubName={storage?.club_name ?? null}
                    updatePath={paths.update}
                    eventEditPath={event.edit_path}
                  />
                </Flex>
              </Tabs.Content>
            </Tabs.Root>
          )}
        </Flex>
      </Container>
    </>
  );
}
